import "./App.css"
import { Admin, CustomRoutes, Layout, Menu, Resource, AppBar, GetListParams, useSidebarState, useRefresh } from "react-admin"
import { Navigate, Route } from "react-router-dom"
import buildHasuraProvider from "ra-data-hasura"
import { ComponentType, Fragment, LazyExoticComponent, ReactElement, Suspense, lazy, useEffect, useState } from "react"
import { ApolloClient, InMemoryCache, NormalizedCacheObject, DefaultOptions } from "@apollo/client"
import { customBuildFields } from "./hasura"
import { useAuth0 } from "@auth0/auth0-react"
import { Auth0Provider } from "@auth0/auth0-react"
import { ENV } from "./configs/env"
import { ApolloProviderComposer } from "./providers/apollo"
import { ToastContainer } from "react-toastify"
import { GET_MERCHANT_AVERAGE_RATING } from "./queries/merchant"
import { GET_KOL_AVERAGE_RATING } from "./queries/kol-profile"
import { GET_TRANCHES_LOCATION } from "./queries/campaign"
import { GET_TALENT_MANAGER, GET_TALENT_MANAGERS } from "./queries/talent-manager"
import {
  GET_TRANSACTION_HISTORY,
  GET_ADJUSTMENT_TRANSACTIONS,
  GET_KOLS,
  GET_WALLET_ID_BY_KOL_ID,
  GET_KOLS_BY_WALLET_ID,
  GET_CAMPAIGNS_IDS_BY_TXN_IDS,
  GET_CAMPAIGN_INFO_BY_IDS,
  GET_DOCUMENTS_IDS_BY_TXN_IDS,
  GET_DOCUMENTS_BY_IDS,
  GET_TRANSACTION_BY_IDS,
  GET_TRANCHE_INFO_BY_CAMPAIGN_IDS,
  GET_MULTIPLE_KOL_PAYOUTS,
  GET_TRANSATIONS_TIMESTAMP,
  GET_KOL_USER_IDS,
  GET_KOL_USER_EMAILS,
  GET_PAYOUT_ORIGINS_FOR_TRANSFERS,
} from "./queries/transaction"
import { parseExchangeRatesSnapshot } from "./utils/exchangeRates"
import { ResourceType } from "./constants"
import { useRole } from "./hooks/useRole"
import { ReactComponent as IconUsers } from "./assets/nav-users.svg"
import { ReactComponent as IconStar } from "./assets/nav-star.svg"
import { ReactComponent as IconStore } from "./assets/nav-store.svg"
import { ReactComponent as IconMegaphone } from "./assets/nav-megaphone.svg"
import { ReactComponent as IconClock } from "./assets/nav-clock.svg"
import { ReactComponent as IconSettings } from "./assets/nav-settings.svg"
import { ReactComponent as IconTransactionsInActive } from "./assets/ic_transactions-inactive.svg"
import { ReactComponent as OpsLogo } from "./assets/ops-logo.svg"
import { ReactComponent as IconRefresh } from "./assets/ic-refresh.svg"
import { Box, Button, ButtonBase, IconButton, Stack, Typography } from "@mui/material"
import { useDispatch } from "react-redux"
import { setTransactions } from "./store/actions/transactions"
import { setKolQuery } from "./store/actions/kolQuery"
import { LoadingSpinner } from "./shared/components/LoadingSpinner"
import { DATE_TIME_FORMAT } from "./utils/constants"
import { formatDate } from "./utils/time"

const lazyNamed = <T extends ComponentType<any>>(
  loader: () => Promise<Record<string, T>>,
  exportName: string,
): LazyExoticComponent<T> =>
  lazy(async () => {
    const module = await loader()
    return { default: module[exportName] }
  })

const withSuspense = <P extends object>(Component: ComponentType<P>) => {
  return (props: P) => (
    <Suspense fallback={<LoadingSpinner />}>
      <Component {...props} />
    </Suspense>
  )
}

const KolProfilesList = withSuspense(lazyNamed(() => import("./features/KolsList"), "KolProfilesList"))
const KolProfileForm = withSuspense(lazyNamed(() => import("./forms/KolProfileForm"), "KolProfileForm"))
const MerchantsList = withSuspense(lazyNamed(() => import("./features/MerchantsList"), "MerchantsList"))
const MerchantForm = withSuspense(lazyNamed(() => import("./forms/MerchantForm"), "MerchantForm"))
const CampaignsList = withSuspense(lazyNamed(() => import("./features/CampaignsList"), "CampaignsList"))
const CampaignForm = withSuspense(lazyNamed(() => import("./forms/CampaignForm"), "CampaignForm"))
const ActualPayouts = withSuspense(lazyNamed(() => import("./payouts/ActualPayouts"), "ActualPayouts"))
const RevertPayout = withSuspense(lazyNamed(() => import("./forms/RevertForm"), "RevertPayout"))
const TalentsList = withSuspense(lazyNamed(() => import("./features/TalentsList"), "TalentsList"))
const TalentForm = withSuspense(lazyNamed(() => import("./forms/TalentForm"), "TalentForm"))
const SettingsList = withSuspense(lazyNamed(() => import("./features/SettingsList"), "SettingsList"))
const PendingPayouts2 = withSuspense(lazy(() => import("./payouts/PendingPayouts2")))

const createApolloClient = (idToken: string) => {
  console.info(`[createApolloClient]`, { idToken })
  const defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: "no-cache",
      errorPolicy: "ignore",
    },
    query: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  }
  localStorage.setItem("access_token", idToken)
  return new ApolloClient({
    uri: ENV.REACT_APP_APOLLO_GRAPHQL_URL,
    cache: new InMemoryCache(),
    defaultOptions: defaultOptions,
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })
}

// The nav icons inherit `currentColor`, so the active item tints icon and label together.
// `MenuItemLink` renders its label as a bare text node (no ListItemText), so the label is wrapped
// in a span here — that is the only thing CSS can hide when the rail is collapsed.
// Note the selectors: react-admin only puts a static class on the *active* item and on the icon
// slot ("RaMenuItemLink-active" / "-icon"). There is no "RaMenuItemLink-root" class, so the item
// itself has to be reached through MUI's own "MuiMenuItem-root". `&&` raises specificity above
// MenuItem's default styles.
const menuSx = (collapsed: boolean) => ({
  backgroundColor: "#ffffff",
  height: "100%",
  paddingTop: "16px",

  "&& .MuiMenuItem-root": {
    minHeight: 44,
    margin: collapsed ? "4px 6px" : "4px 20px",
    padding: "12px",
    borderRadius: "8px",
    justifyContent: collapsed ? "center" : "flex-start",
    color: "#717171",
    backgroundColor: "transparent",

    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  "&& .MuiMenuItem-root.RaMenuItemLink-active": {
    color: "#5770c6",
    backgroundColor: "#5770c61f",

    "&:hover": {
      backgroundColor: "#5770c61f",
    },
  },

  // Icons are drawn with `currentColor`, so they follow the item's colour.
  "&& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: collapsed ? 0 : 32,
    justifyContent: "center",

    "& svg": {
      width: 20,
      height: 20,
      color: "inherit",
    },
  },

  "&& .nav-label": {
    display: collapsed ? "none" : "inline",
    color: "inherit",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    whiteSpace: "nowrap",
  },
})

type NavItemProps = { to: string; label: string; icon: ReactElement }

const navItem = ({ to, label, icon }: NavItemProps) => (
  <Menu.Item key={to} to={to} primaryText={label} leftIcon={icon}>
    <span className="nav-label">{label}</span>
  </Menu.Item>
)

export const MyMenu = () => {
  const userRole = useRole()
  const [sidebarOpen] = useSidebarState()

  const accountantItems = [
    navItem({ to: "/talent_manager", label: "Talent Managers", icon: <IconUsers /> }),
    navItem({ to: "/kol_profile", label: "Influencers", icon: <IconStar /> }),
    navItem({ to: "/kol_payout", label: "Pending Payouts", icon: <IconClock /> }),
    navItem({ to: "/view_transaction_for_payout", label: "Transactions Log", icon: <IconTransactionsInActive /> }),
  ]

  const campaignManagerItems = [
    navItem({ to: "/talent_manager", label: "Talent Managers", icon: <IconUsers /> }),
    navItem({ to: "/kol_profile", label: "Influencers", icon: <IconStar /> }),
    navItem({ to: "/client_profile", label: "Merchants", icon: <IconStore /> }),
    navItem({ to: "/campaign", label: "Campaigns", icon: <IconMegaphone /> }),
    navItem({ to: "/kol_payout", label: "Pending Payouts", icon: <IconClock /> }),
    navItem({ to: "/settings", label: "Settings", icon: <IconSettings /> }),
  ]

  return (
    <Menu sx={menuSx(!sidebarOpen)}>
      {userRole === "accountant" ? accountantItems : null}
      {userRole === "campaign_manager" ? campaignManagerItems : null}
    </Menu>
  )
}

function roleToText(role: any) {
  if (role === "accountant") {
    return "Accountant"
  } else if (role === "campaign_manager") {
    return "Platform Manager"
  } else {
    return ""
  }
}

const MyAppBar = () => {
  const { user, logout } = useAuth0()
  const role = useRole()
  const [sidebarOpen, setSidebarOpen] = useSidebarState()
  const refresh = useRefresh()

  return (
    <AppBar
      userMenu={false}
      sx={{
        backgroundColor: "#ffffff",
        color: "#111827",
        boxShadow: "none",
        borderBottom: "1px solid #e5e7eb",
        // The logo doubles as the sidebar toggle, so react-admin's own hamburger is hidden.
        "& .RaAppBar-menuButton": { display: "none" },
        "& .RaAppBar-toolbar": { minHeight: 64 },
        // The default refresh icon is replaced by a custom one in the toolbar (see below).
        "& .RaLoadingIndicator-loadedIcon": { display: "none" },
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
        <ButtonBase
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? "Collapse menu" : "Expand menu"}
          sx={{ borderRadius: "6px", p: 0.5, "& svg": { height: 22, width: "auto", display: "block" } }}
        >
          <OpsLogo title="KOLs.Asia Operation Tool" />
        </ButtonBase>

        <Box sx={{ flex: 1 }} />

        {user && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography sx={{ color: "#374151", fontSize: { xs: "0.85rem", sm: "0.95rem" } }}>
              Hi, {user?.name} {roleToText(role) ? `(${roleToText(role)})` : ""}
            </Typography>

            <Button
              variant="outlined"
              onClick={() => {
                localStorage.removeItem("access_token")
                logout({ returnTo: window.location.origin })
              }}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                height: "35px",
                padding: "9px 18px",
                minWidth: 0,
                border: "1px solid #5770C6",
                borderRadius: "6px",
                textTransform: "none",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "17px",
                color: "#5770C6",
                whiteSpace: "nowrap",
                "&:hover": { border: "1px solid #5770C6", backgroundColor: "rgba(87, 112, 198, 0.08)" },
              }}
            >
              Log out
            </Button>

            <IconButton
              onClick={() => refresh()}
              aria-label="Refresh"
              sx={{ p: 1, "& svg": { width: 18, height: 18, display: "block" } }}
            >
              <IconRefresh />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </AppBar>
  )
}

// react-admin sizes the layout root to `min-width: fit-content` and leaves the content column as
// a flex item with the default `min-width: auto`, so neither can shrink below the widest thing
// inside them. A wide table (Transactions Log) therefore stretched the whole document instead of
// scrolling inside its own container — and because the app bar and the left menu are fixed,
// scrolling sideways slid the table underneath the menu. Letting both shrink keeps the horizontal
// scroll where it belongs: inside the table.
const layoutSx = {
  minWidth: 0,
  "& .RaLayout-contentWithSidebar": { minWidth: 0 },
  "& .RaLayout-content": { minWidth: 0 },
}

export const MyLayout = (props: any) => <Layout appBar={MyAppBar} {...props} menu={MyMenu} sx={layoutSx} />

export const Entrance = () => {
  return (
    <Auth0Provider
      domain={ENV.REACT_APP_AUTH0_DOMAIN}
      clientId={ENV.REACT_APP_AUTH0_CLIENT_ID}
      redirectUri={window.location.origin}
    >
      <ApolloProviderComposer>
        <AuthProvider />
        <ToastContainer />
      </ApolloProviderComposer>
    </Auth0Provider>
  )
}

async function getKolsByWalletIds(client: ApolloClient<NormalizedCacheObject>, walletIds: Array<any>) {
  const walletIdResponse = await client.query({
    query: GET_KOLS_BY_WALLET_ID,
    variables: {
      wallet_ids: walletIds,
    },
  })
  return walletIdResponse?.data?.kol_profile
}

async function getDocumentsByIdsByTnxIds(client: ApolloClient<NormalizedCacheObject>, tnxIds: Array<any>) {
  const documentIdsResponse = await client.query({
    query: GET_DOCUMENTS_IDS_BY_TXN_IDS,
    variables: {
      ids: tnxIds,
    },
  })

  let result = documentIdsResponse.data.transaction

  const documentIds = documentIdsResponse.data.transaction
    .map((transaction: any) => transaction.document_id)
    .filter((documentId: any) => documentId !== null && documentId !== "" && documentId !== undefined)

  const documentResponse = await client.query({
    query: GET_DOCUMENTS_BY_IDS,
    variables: {
      ids: documentIds,
    },
  })
  result = result.map((transaction: any) => {
    const document = documentResponse.data.file_metadata.find((doc: any) => doc.id === transaction.document_id)
    return {
      ...transaction,
      document_name: document ? document.original_name : "",
      document_link: document ? document.url : "",
      document_id: document ? document.id : "",
    }
  })
  return result
}

async function getMerchantRatings(client: ApolloClient<NormalizedCacheObject>, merchants: Array<any>) {
  const merchantIds = merchants.map((merchant: any) => merchant.id)
  const response = await client.query({
    query: GET_MERCHANT_AVERAGE_RATING,
    variables: {
      ids: merchantIds,
    },
  })
  return merchants.map((merchant: any) => {
    const rating = response.data.merchant_ratings_stats.find((rating: any) => rating.client_profile_id === merchant.id)
    return {
      ...merchant,
      average_rating: rating?.average_rating ?? 0,
    }
  })
}

async function getCampaignWithTranchesLocation(client: ApolloClient<NormalizedCacheObject>, campaigns: Array<any>) {
  const campaignIds = campaigns.map((campaign: any) => campaign.id)
  const response = await client.query({
    query: GET_TRANCHES_LOCATION,
    variables: {
      campaignIds: campaignIds,
    },
  })

  return campaigns.map((campaign: any) => {
    // replace original tranche of the campaign with tranches from the result
    const tranches = response.data.campaign.find((c: any) => c.id === campaign.id)?.tranches ?? []
    return {
      ...campaign,
      tranches: tranches,
    }
  })
}

async function getKolRatings(client: ApolloClient<NormalizedCacheObject>, kols: Array<any>) {
  const kolIds = kols.map((kol: any) => kol.id)
  const response = await client.query({
    query: GET_KOL_AVERAGE_RATING,
    variables: {
      ids: kolIds,
    },
  })
  return kols.map((kol: any) => {
    const rating = response.data.kol_ratings_stats.find((rating: any) => rating.kol_profile_id === kol.id)
    return {
      ...kol,
      average_rating: rating?.average_rating ?? 0,
    }
  })
}

async function getCampaignsInfoByTxnIds(client: ApolloClient<NormalizedCacheObject>, txnIds: Array<any>) {
  const txnResponse = await client.query({
    query: GET_CAMPAIGNS_IDS_BY_TXN_IDS,
    variables: {
      ids: txnIds,
    },
  })
  const campaignIds = txnResponse?.data?.transaction
    .map((transaction: any) => transaction.campaign_id)
    .filter((campaignId: any) => campaignId !== null)
  const campaignInfoResponse = await client.query({
    query: GET_CAMPAIGN_INFO_BY_IDS,
    variables: {
      ids: campaignIds,
    },
  })
  const trancheInfoResponse = await client.query({
    query: GET_TRANCHE_INFO_BY_CAMPAIGN_IDS,
    variables: {
      campaignIds: campaignIds,
    },
  })
  let result = []
  for (let i = 0; i < txnResponse?.data?.transaction.length; i++) {
    const txn = txnResponse?.data?.transaction[i]
    const campaigns = campaignInfoResponse?.data?.campaign.filter(
      (campaign: any) => campaign.id === txn.campaign_id || campaign.id === txn.reference_transaction_id,
    )

    let campaignInfo = ""
    for (let j = 0; j < campaigns.length; j++) {
      const tranches = trancheInfoResponse?.data?.tranche.filter((tranche: any) => campaigns[j].id === tranche.campaign_id)
      campaignInfo += campaigns[j].name + " | " + tranches.map((tranche: any) => tranche.name).join(" | ")
      if (j < campaigns.length - 1) {
        campaignInfo += " - "
      }
    }
    result.push({
      ...txn,
      campaign_id: campaigns.map((campaign: any) => campaign.id).join(","),
      campaign_name: campaignInfo,
    })
  }

  return result
}

async function getAdjustedTransactions(client: ApolloClient<NormalizedCacheObject>, txn_ids: Array<any> = []) {
  const adjustmentTransactions = await client.query({
    query: GET_ADJUSTMENT_TRANSACTIONS,
  })

  const normalTransactions = await client.query({
    query: GET_TRANSACTION_BY_IDS,
    variables: {
      ids: txn_ids,
    },
  })

  const kols = await client.query({
    query: GET_KOLS,
  })

  let adjustmentTransactionsResult = adjustmentTransactions.data.transaction.map((transaction: any) => {
    const kol = kols.data.kol_profile.find(
      (kol: any) => kol.wallet_id === transaction.debit || kol.wallet_id === transaction.credit,
    )
    return {
      ...transaction,
      individual_fullname: kol ? kol.individual_fullname : "N/A",
      kol_wallet_id: kol ? kol.wallet_id : "N/A",
      kol_name: `${kol ? kol.first_name : ""}` + " " + `${kol ? kol.last_name : ""}`,
      // KA-1962: fallback currency for rows with no payout to inherit from (manual adjustments,
      // reverts, transfers created before the payout link existed).
      kol_display_currency: kol?.display_currency ?? null,
    }
  })
  adjustmentTransactionsResult = (adjustmentTransactionsResult as Array<any>).filter(
    (transaction: any) => transaction.individual_fullname !== "N/A",
  )
  let normalTransactionsResult = normalTransactions.data.transaction.map((transaction: any) => {
    const kol = kols.data.kol_profile.find(
      (kol: any) => kol.wallet_id === transaction.debit || kol.wallet_id === transaction.credit,
    )
    return {
      ...transaction,
      individual_fullname: kol ? kol.individual_fullname : "N/A",
      kol_wallet_id: kol ? kol.wallet_id : "N/A",
      kol_name: `${kol ? kol.first_name : ""}` + " " + `${kol ? kol.last_name : ""}`,
      // KA-1962: fallback currency for rows with no payout to inherit from (manual adjustments,
      // reverts, transfers created before the payout link existed).
      kol_display_currency: kol?.display_currency ?? null,
    }
  })
  normalTransactionsResult = (normalTransactionsResult as Array<any>).filter(
    (transaction: any) => transaction.individual_fullname !== "N/A",
  )
  const combinedTransactions = [...adjustmentTransactionsResult, ...normalTransactionsResult]
  return combinedTransactions
}

/**
 * KA-1962 — decide which currency each Transaction Log row is read in, and with which FX rate.
 *
 * `amount_usd_cents` is genuinely USD cents platform-wide, so every row needs a currency and a
 * USD→currency rate to be displayed. Neither lives on the row itself:
 *
 * 1. Preferred — the `KOL_PAYOUT` that references this transfer carries the payout `currency` and,
 *    through its campaign, the FX snapshot pinned at campaign creation. This is the accurate path
 *    and covers every transfer made since the payout link started being written.
 * 2. Fallback — no payout points here (manual adjustments, reverts, pre-link transfers): fall back
 *    to the payee's `display_currency` and let the caller convert with live rates.
 *
 * Returns each transaction with `resolved_currency` and `exchange_rates_snapshot` attached.
 */
async function attachPayoutCurrency(client: ApolloClient<NormalizedCacheObject>, transactions: Array<any>) {
  if (transactions.length === 0) return transactions

  let origins: Array<any> = []
  try {
    const { data } = await client.query({
      query: GET_PAYOUT_ORIGINS_FOR_TRANSFERS,
      variables: { ids: transactions.map((transaction: any) => transaction.id) },
    })
    origins = data?.transaction ?? []
  } catch (e) {
    // Degrade to the payee's currency rather than dropping the whole log.
    console.error("[App] Failed to resolve payout currency for transactions", e)
  }

  // A transfer can be referenced by more than one payout (one per settled milestone); they always
  // belong to the same campaign, so the first one is representative.
  const originByTransferId = new Map<string, any>()
  origins.forEach((origin: any) => {
    if (origin.reference_transaction_id && !originByTransferId.has(origin.reference_transaction_id)) {
      originByTransferId.set(origin.reference_transaction_id, origin)
    }
  })

  return transactions.map((transaction: any) => {
    const origin = originByTransferId.get(transaction.id)
    return {
      ...transaction,
      resolved_currency: origin?.currency || transaction.kol_display_currency || "USD",
      exchange_rates_snapshot: parseExchangeRatesSnapshot(origin?.campaign?.exchange_rates),
    }
  })
}

async function getKolUserEmail(client: ApolloClient<NormalizedCacheObject>, kolIds: Array<any>) {
  const userIds = await client.query({
    query: GET_KOL_USER_IDS,
    variables: {
      kol_profile_id: kolIds,
    },
  })

  const kolWithUserId = userIds.data.kol_profile
    .filter((kol: any) => kol.user_id)
    .map((kol: any) => {
      return {
        id: kol.id,
        user_id: kol.user_id,
      }
    })

  if (kolWithUserId.length === 0) {
    return []
  }

  const kolUserEmails = await client.query({
    query: GET_KOL_USER_EMAILS,
    variables: {
      user_ids: kolWithUserId.map((kol: any) => kol.user_id),
    },
  })
  const kolUserEmailsResult = kolWithUserId.map((kol: any) => {
    const email = kolUserEmails.data.user.find((user: any) => user.id === kol.user_id)?.email ?? ""
    const userId = kolUserEmails.data.user.find((user: any) => user.id === kol.user_id)?.id ?? ""
    return {
      kol_id: kol.id,
      email: email,
      user_id: userId,
    }
  })

  return kolUserEmailsResult
}

const UnauthorizedPage = () => {
  const { logout } = useAuth0()

  const handleSignOut = () => {
    localStorage.removeItem("access_token")
    logout({ returnTo: window.location.origin })
  }

  return (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography variant="body1" gutterBottom>
        This account doesn't have permission to use this website. Please contact admin for more detail.
      </Typography>
      <Button variant="contained" color="primary" onClick={handleSignOut}>
        Sign Out
      </Button>
    </Box>
  )
}

const App = ({ token }: { token: string }) => {
  const dispatch = useDispatch()
  const [dataProvider, setDataProvider] = useState(null)
  const userRole = useRole()
  const client = createApolloClient(token)
  dispatch(setKolQuery(""))

  const getKolsTransactionTimestamp = async (kolIds: string[]) => {
    const payouts = await client.query({
      query: GET_MULTIPLE_KOL_PAYOUTS,
      variables: {
        kol_profile_id: kolIds,
      },
    })

    const payoutIds = payouts.data.kol_payout.map((payout: any) => payout.id)
    const transactions = await client.query({
      query: GET_TRANSATIONS_TIMESTAMP,
      variables: {
        credits: payoutIds,
      },
    })
    var mappedKolTransactions = []
    for (let kolId of kolIds) {
      const kolPayouts = payouts.data.kol_payout.filter((payout: any) => payout.kol_profile_id === kolId)
      const kolTransactions =
        transactions.data.transaction.filter((transaction: any) =>
          kolPayouts.map((payout: any) => payout.id).includes(transaction.credit),
        ) ?? []
      // get transaction with latest timestamp
      kolTransactions.sort((a: any, b: any) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      })

      mappedKolTransactions.push({
        kol_profile_id: kolId,
        transactions: kolTransactions,
      })
    }
    return mappedKolTransactions
  }

  const buildDataProvider = async () => {
    const client = createApolloClient(token)

    const dataProvider = await buildHasuraProvider(
      {
        client,
      },
      { buildFields: customBuildFields },
    )

    setDataProvider((introspection): any => {
      console.info(`[App]`, { introspection, dataProvider })
      return {
        ...dataProvider,
        getList: async (resource: any, params: any) => {
          if (resource === ResourceType.kol_payout) {
            return {
              data: [],
              total: 0,
            }
          }

          if (resource === ResourceType.kol_profile) {
            var paymentInfo = new Array()
            var result = await dataProvider.getList(resource, params)
            const kolProfileIds = result.data.map((kolProfile: any) => `${kolProfile.id}`)

            const kolProfilesWithoutEmail = result.data.filter((kolProfile: any) => !kolProfile.email || kolProfile.email === "")
            const kolProfileIdsWithoutEmail = kolProfilesWithoutEmail.map((kolProfile: any) => `${kolProfile.id}`)

            const kolUserEmails =
              kolProfileIdsWithoutEmail.length > 0 ? await getKolUserEmail(client, kolProfileIdsWithoutEmail) : []

            const mappedTransation = await getKolsTransactionTimestamp(kolProfileIds)
            const customFilter = params?.custom_filter ?? {}
            const nameOrEmailFilter = customFilter.query ?? ""
            const talentManagerId = customFilter.talent_manager_id
            for (var i = 0; i < result.data.length; i++) {
              const element = result.data[i]
              const transactions = mappedTransation.find((transaction: any) => transaction.kol_profile_id === element.id)
              element.payouts = transactions?.transactions ?? []
              const fullName = element.first_name + " " + element.last_name
              const email = element.email ?? ""
              if (talentManagerId && talentManagerId !== "") {
                if (element.talent_manager?.id !== talentManagerId) {
                  continue
                }
              }

              if (nameOrEmailFilter && nameOrEmailFilter !== "") {
                if (
                  fullName.toLowerCase().includes(nameOrEmailFilter.toLowerCase()) ||
                  email.toLowerCase().includes(nameOrEmailFilter.toLowerCase())
                ) {
                  paymentInfo.push(element)
                  continue
                }
                if (kolUserEmails.length > 0) {
                  const kolEmail = kolUserEmails.find((kol: any) => kol.kol_id === element.id)
                  if (kolEmail && kolEmail.email.toLowerCase().includes(nameOrEmailFilter.toLowerCase())) {
                    paymentInfo.push(element)
                    continue
                  }
                }
              } else {
                paymentInfo.push(element)
              }
            }

            //Sort KOLs by created_at, descending
            paymentInfo.sort((a: any, b: any) => {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            })
            if (userRole === "campaign_manager") {
              paymentInfo = await getKolRatings(client, paymentInfo)
            }
            return {
              data: paymentInfo,
              total: paymentInfo.length,
            }
          } else if (resource === ResourceType.myTransactionHistory) {
            const { profile_id } = params?.filter || {}

            if (!profile_id) {
              return {
                data: [],
                total: 0,
              }
            }

            const resp = await client.query({
              query: GET_TRANSACTION_HISTORY,
              variables: {
                profile_id,
                profile_type: "MERCHANT",
              },
            })

            const list = resp?.data?.myTransactionHistory || []

            return {
              data: list?.map((item: any) => {
                item.timestamp = formatDate(item?.timestamp, DATE_TIME_FORMAT)
                return item
              }),
              total: list.length,
            }
          } else if (resource === ResourceType.viewTransactionForPayout) {
            const kolTransactionParams = {
              pagination: {
                page: 1,
                perPage: 10000,
              },
              sort: {
                field: "individual_fullname",
                order: "ASC" as const,
              },
            }
            const data = await dataProvider.getList(resource, kolTransactionParams as GetListParams)
            const adjustmentTransactions = await getAdjustedTransactions(
              client,
              data.data.map((transaction: any) => transaction.id),
            )
            let list = Array()
            adjustmentTransactions.forEach((element) => {
              list.push(element)
            })
            const transactionFilter = params?.filter ?? {}
            if (transactionFilter.type != "") {
              list = list.filter((transaction: any) => transaction.type === transactionFilter.type)
            }
            if (transactionFilter.kol_id != "") {
              const walletIdResponse = await client.query({
                query: GET_WALLET_ID_BY_KOL_ID,
                variables: {
                  kol_id: transactionFilter.kol_id,
                },
              })
              const walletId = walletIdResponse?.data?.kol_profile[0]?.wallet_id
              list = list.filter((transaction: any) => transaction.kol_wallet_id === walletId)
            }
            list.sort((a: any, b: any) => {
              return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            })

            const walletIds = list.map((transaction: any) => transaction.kol_wallet_id)
            const kols = await getKolsByWalletIds(client, walletIds)
            let includeRefTxnIds = [
              ...list.map((transaction: any) => transaction.id),
              ...list
                .filter((transaction: any) => transaction.reference_transaction_id)
                .map((transaction: any) => transaction.reference_transaction_id),
            ]
            const campaignInfo = await getCampaignsInfoByTxnIds(client, includeRefTxnIds)
            list = list.map((transaction: any) => {
              const kol = kols.find((kol: any) => kol.wallet_id === transaction.kol_wallet_id)
              const campaignName = campaignInfo.find((c: any) => c.id === transaction.id)?.campaign_name ?? ""
              return {
                ...transaction,
                bank_name: kol ? kol.bank_name : "",
                bank_account_no: kol ? kol.bank_account_no : "",
                campaign_info:
                  transaction.type === "KOL_TRANSFER" || transaction.type === "KOL_TRANSFER_REVERT" || campaignName === ""
                    ? transaction.description
                    : campaignInfo.find((c: any) => c.id === transaction.id)?.campaign_name ?? "",
                campaign_id: campaignInfo.find((c: any) => c.id === transaction.id)?.campaign_id ?? "",
              }
            })

            const documents = await getDocumentsByIdsByTnxIds(
              client,
              list.map((transaction: any) => transaction.id),
            )
            list = list.map((transaction: any) => {
              const document = documents.find((doc: any) => doc.id === transaction.id)
              return {
                ...transaction,
                document_name: document ? document.document_name : "",
                document_link: document ? document.document_link : "",
                document_id: document ? document.document_id : "",
              }
            })

            // KA-1962: resolve the currency each row should be read in, plus the FX snapshot to
            // convert it with. Done here (not per row) so the table and the CSV export — which
            // reads this same list out of redux — always agree.
            list = await attachPayoutCurrency(client, list)

            dispatch(setTransactions(list))
            return {
              data: getSubArray(list, (params.pagination.page - 1) * params.pagination.perPage, params.pagination.perPage),
              total: list.length,
            }
          } else if (resource === ResourceType.client_profile) {
            const result = await dataProvider.getList(resource, params)
            const merchantWithRating: any = await getMerchantRatings(client, result.data)
            console.info(`[App] Client Profile Data`, merchantWithRating)
            const finalResult = {
              data: merchantWithRating,
              total: result.total,
            }
            console.info(`[App] Client Profile`, { finalResult })
            return finalResult
          } else if (resource === ResourceType.campaign) {
            const result = await dataProvider.getList(resource, params)
            const campaignWithTrancheLocation: any = await getCampaignWithTranchesLocation(client, result.data)
            const finalResult = {
              data: campaignWithTrancheLocation,
              total: result.total,
            }
            return finalResult
          } else if (resource === ResourceType.talent_manager) {
            try {
              const result = await client.query({
                query: GET_TALENT_MANAGERS,
                variables: {
                  search: "%%",
                },
                fetchPolicy: "network-only",
              })
              return {
                data: result.data?.talent_manager || [],
                total: result.data?.talent_manager?.length || 0,
              }
            } catch (error) {
              console.error("Error in talent_manager getList:", error)
              return {
                data: [],
                total: 0,
              }
            }
          } else {
            return dataProvider.getList(resource, params)
          }
        },
        getOne: async (resource: any, params: any) => {
          if (resource === ResourceType.talent_manager) {
            try {
              const result = await client.query({
                query: GET_TALENT_MANAGER,
                variables: { id: params.id },
                fetchPolicy: "network-only",
              })
              return {
                data: result.data?.talent_manager_by_pk,
              }
            } catch (error) {
              throw error
            }
          }
          return dataProvider.getOne(resource, params)
        },
      }
    })
  }

  useEffect(() => {
    buildDataProvider()
  }, [token])

  useEffect(() => {
    console.info(`[ENV]`, process.env)
  }, [])

  if (!dataProvider) {
    return <div>No data provider</div>
  }

  if (!userRole) {
    return null
  }

  if (userRole !== "accountant" && userRole !== "campaign_manager" && userRole !== "backoffice") {
    return <UnauthorizedPage />
  }

  return (
    <Admin layout={MyLayout} dataProvider={dataProvider as any}>
      {(userRole === "accountant" || userRole === "backoffice") && (
        <Fragment>
          <Resource name="kol_profile" list={KolProfilesList} edit={KolProfileForm} />
          <Resource name="kol_payout" list={PendingPayouts2} />
          <Resource name="view_transaction_for_payout" list={ActualPayouts} edit={RevertPayout} />
          <Resource name="talent_manager" list={TalentsList} edit={TalentForm} />
          {/* KA-1910 — Settings is Platform Manager only, so the resource is not registered
              here at all. Hiding the nav item is not a control on its own: an Accountant
              typing /settings is redirected to their default page by the route below, and
              Hasura denies `config`/`feature_flags` to the role as the real backstop. */}
          <CustomRoutes>
            <Route path="/settings/*" element={<Navigate to="/" replace />} />
          </CustomRoutes>
        </Fragment>
      )}

      {userRole === "campaign_manager" && (
        <Fragment>
          <Resource name="kol_profile" list={KolProfilesList} edit={KolProfileForm} />
          <Resource name="client_profile" list={MerchantsList} edit={MerchantForm} />
          <Resource name="campaign" list={CampaignsList} edit={CampaignForm} />
          <Resource name="kol_payout" list={PendingPayouts2} />
          <Resource name="talent_manager" list={TalentsList} edit={TalentForm} />
          <Resource name="settings" list={SettingsList} />
        </Fragment>
      )}
    </Admin>
  )
}

const AuthProvider: any = () => {
  const { isLoading, isAuthenticated, error, loginWithRedirect, logout, getIdTokenClaims } = useAuth0()

  const [token, setToken] = useState<null | string>(null)

  useEffect(() => {
    if (isAuthenticated) {
      getIdTokenClaims().then((idToken) => {
        console.info(`[AuthProvider] got ID token ${JSON.stringify(idToken)}`)

        setToken(idToken?.__raw as string)
      })
    }
  }, [isAuthenticated, token])

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      console.log("Redirect")
      ;(async () => {
        await loginWithRedirect()
      })()
    }
  }, [isLoading])

  const onTryAgain = () => {
    localStorage.removeItem("access_token")
    logout({ returnTo: window.location.origin })
  }

  console.info(`[AuthProvider]`, { isAuthenticated, isLoading, error, token })

  if (isLoading) {
    return <LoadingSpinner />
  }
  if (error) {
    return (
      <div>
        <h1>Oops... {error.message}</h1>
        <button onClick={onTryAgain}>TRY AGAIN</button>
      </div>
    )
  }

  return <div>{!token ? <LoadingSpinner /> : <App token={token} />}</div>
}

function getSubArray<T>(array: T[], fromIndex: number, lengthOfSubArray: number): T[] {
  let endIndex = fromIndex + lengthOfSubArray
  const subArray = array.slice(fromIndex, endIndex)
  return subArray
}
